import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { DealerMasterState } from './types'
import { DealerMasterService } from '../../services/dealer-master/DealerMasterService'

const initialState: DealerMasterState = {
  // state
  isGetAllDealersLoading: false,
  isCreateDealerLoading: false,
  isDeleteDealerLoading: false,
  isGetDealerInfoByIdLoading: false,
  isGetDealerColorCodeLoading: false,
  isGetDealerAddressLoading: false,
  isGetDealerRemarkLoading: false,
  isGetDealerStatusLoading: false,
  isUpdateDealerInfoByIdLoading: false,
  isUpdateDealerColorCodeLoading: false,
  isUpdateDealerAddressLoading: false,
  isUpdateDealerRemarkLoading: false,
  isUpdateStatusDealerLoading: false,
  isGetCountTransactionLoading: false,
  isGetAllDealerTransactionsLoading: false,
  isExportDealerTransactionsLoading: false,
  isExportDealersLoading: false,
  isGetTransactionInsuredLoading: false,
  isGetTransactionSummaryLoading: false,
  isGetTransactionDetailLoading: false,
  isUpdateDealerTransactionInsuredLoading: false,
  isCancelDealerInsuredLoading: false,
  isGetTransactionCommissionLoading: false,
  isGetDealerTransactionTaxInvoiceLoading: false,
  isUpdateDealerTransactionTaxInvoiceLoading: false,
  isUpdateDealerSentTaxInvoiceLoading: false,
  dealerImage: null,
  dealerColorCodes: [],
  isActive: true,
  isEditInfo: false,
  // fetched
  fetchedTransactionInsured: []
}

const slice = createSlice({
  name: 'dealer-master',
  initialState,
  reducers: {
    toggleDealerActive: (state) => {
      state.isActive = !state.isActive
    },
    setDealerImage: (state, action: PayloadAction<File>) => {
      state.dealerImage = action.payload
    },
    onChangeDealerColorCodes: (state, action: PayloadAction<{ colorCodes: string; index: number }>) => {
      const newColorCode = action.payload.colorCodes
      const updatedColorCodes = [...state.dealerColorCodes]
      updatedColorCodes[action.payload.index] = newColorCode
      state.dealerColorCodes = updatedColorCodes
    },
    setIsEditInfo: (state, action: PayloadAction<boolean>) => {
      state.isEditInfo = action.payload
    },
    resetDealerMasterState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(DealerMasterService.getAllDealersAsyncThunk.pending, (state) => {
        state.isGetAllDealersLoading = true
      })
      .addCase(DealerMasterService.getAllDealersAsyncThunk.rejected, (state) => {
        state.isGetAllDealersLoading = false
      })
      .addCase(DealerMasterService.getAllDealersAsyncThunk.fulfilled, (state, action) => {
        state.isGetAllDealersLoading = false
        state.fetchedAllDealers = action.payload
      })
      .addCase(DealerMasterService.createDealerAsyncThunk.pending, (state) => {
        state.isCreateDealerLoading = true
      })
      .addCase(DealerMasterService.createDealerAsyncThunk.rejected, (state) => {
        state.isCreateDealerLoading = false
      })
      .addCase(DealerMasterService.createDealerAsyncThunk.fulfilled, (state) => {
        state.isCreateDealerLoading = false
      })
      .addCase(DealerMasterService.deleteDealerAsyncThunk.pending, (state) => {
        state.isDeleteDealerLoading = true
      })
      .addCase(DealerMasterService.deleteDealerAsyncThunk.rejected, (state) => {
        state.isDeleteDealerLoading = false
      })
      .addCase(DealerMasterService.deleteDealerAsyncThunk.fulfilled, (state) => {
        state.isDeleteDealerLoading = false
      })
      .addCase(DealerMasterService.exportDealersAsyncThunk.pending, (state) => {
        state.isExportDealersLoading = true
      })
      .addCase(DealerMasterService.exportDealersAsyncThunk.rejected, (state) => {
        state.isExportDealersLoading = false
      })
      .addCase(DealerMasterService.exportDealersAsyncThunk.fulfilled, (state) => {
        state.isExportDealersLoading = false
      })
      .addCase(DealerMasterService.getDealerInfoByIdAsyncThunk.pending, (state) => {
        state.isGetDealerInfoByIdLoading = true
      })
      .addCase(DealerMasterService.getDealerInfoByIdAsyncThunk.rejected, (state) => {
        state.isGetDealerInfoByIdLoading = false
      })
      .addCase(DealerMasterService.getDealerInfoByIdAsyncThunk.fulfilled, (state, action) => {
        state.isGetDealerInfoByIdLoading = false
        state.fetchedDealerInfoById = action.payload
        state.dealerTimestampDetail = action.payload
      })
      .addCase(DealerMasterService.getDealerColorCodeAsyncThunk.pending, (state) => {
        state.isGetDealerColorCodeLoading = true
      })
      .addCase(DealerMasterService.getDealerColorCodeAsyncThunk.rejected, (state) => {
        state.isGetDealerColorCodeLoading = false
      })
      .addCase(DealerMasterService.getDealerColorCodeAsyncThunk.fulfilled, (state, action) => {
        state.isGetDealerColorCodeLoading = false
        state.fetchedDealerColorCode = action.payload
        if (action.payload.colorCodes === null) {
          state.dealerColorCodes = []
        } else {
          state.dealerColorCodes = action.payload.colorCodes
        }
        state.dealerTimestampDetail = action.payload
      })
      .addCase(DealerMasterService.getDealerAddressAsyncThunk.pending, (state) => {
        state.isGetDealerAddressLoading = true
      })
      .addCase(DealerMasterService.getDealerAddressAsyncThunk.rejected, (state) => {
        state.isGetDealerAddressLoading = false
      })
      .addCase(DealerMasterService.getDealerAddressAsyncThunk.fulfilled, (state, action) => {
        state.isGetDealerAddressLoading = false
        state.fetchedDealerAddress = action.payload
        state.dealerTimestampDetail = action.payload
      })
      .addCase(DealerMasterService.getDealerRemarkAsyncThunk.pending, (state) => {
        state.isGetDealerRemarkLoading = true
      })
      .addCase(DealerMasterService.getDealerRemarkAsyncThunk.rejected, (state) => {
        state.isGetDealerRemarkLoading = false
      })
      .addCase(DealerMasterService.getDealerRemarkAsyncThunk.fulfilled, (state, action) => {
        state.isGetDealerRemarkLoading = false
        state.fetchedDealerRemark = action.payload
        state.dealerTimestampDetail = action.payload
      })
      .addCase(DealerMasterService.getDealerStatusAsyncThunk.pending, (state) => {
        state.isGetDealerStatusLoading = true
      })
      .addCase(DealerMasterService.getDealerStatusAsyncThunk.rejected, (state) => {
        state.isGetDealerStatusLoading = false
      })
      .addCase(DealerMasterService.getDealerStatusAsyncThunk.fulfilled, (state, action) => {
        state.isGetDealerStatusLoading = false
        state.fetchedDealerStatus = action.payload
        state.isActive = action.payload.isActive
        state.dealerTimestampDetail = action.payload
      })
      .addCase(DealerMasterService.updateDealerInfoAsyncThunk.pending, (state) => {
        state.isUpdateDealerInfoByIdLoading = true
      })
      .addCase(DealerMasterService.updateDealerInfoAsyncThunk.rejected, (state) => {
        state.isUpdateDealerInfoByIdLoading = false
      })
      .addCase(DealerMasterService.updateDealerInfoAsyncThunk.fulfilled, (state) => {
        state.isUpdateDealerInfoByIdLoading = false
      })
      .addCase(DealerMasterService.updateDealerColorCodeAsyncThunk.pending, (state) => {
        state.isUpdateDealerColorCodeLoading = true
      })
      .addCase(DealerMasterService.updateDealerColorCodeAsyncThunk.rejected, (state) => {
        state.isUpdateDealerColorCodeLoading = false
      })
      .addCase(DealerMasterService.updateDealerColorCodeAsyncThunk.fulfilled, (state) => {
        state.isUpdateDealerColorCodeLoading = false
      })
      .addCase(DealerMasterService.updateDealerAddressAsyncThunk.pending, (state) => {
        state.isUpdateDealerAddressLoading = true
      })
      .addCase(DealerMasterService.updateDealerAddressAsyncThunk.rejected, (state) => {
        state.isUpdateDealerAddressLoading = false
      })
      .addCase(DealerMasterService.updateDealerAddressAsyncThunk.fulfilled, (state) => {
        state.isUpdateDealerAddressLoading = false
      })
      .addCase(DealerMasterService.updateDealerRemarkAsyncThunk.pending, (state) => {
        state.isUpdateDealerRemarkLoading = true
      })
      .addCase(DealerMasterService.updateDealerRemarkAsyncThunk.rejected, (state) => {
        state.isUpdateDealerRemarkLoading = false
      })
      .addCase(DealerMasterService.updateDealerRemarkAsyncThunk.fulfilled, (state) => {
        state.isUpdateDealerRemarkLoading = false
      })
      .addCase(DealerMasterService.updateStatusDealerAsyncThunk.pending, (state) => {
        state.isUpdateStatusDealerLoading = true
      })
      .addCase(DealerMasterService.updateStatusDealerAsyncThunk.rejected, (state) => {
        state.isUpdateStatusDealerLoading = false
      })
      .addCase(DealerMasterService.updateStatusDealerAsyncThunk.fulfilled, (state) => {
        state.isUpdateStatusDealerLoading = false
      })
      .addCase(DealerMasterService.countTransactionAsyncThunk.pending, (state) => {
        state.isGetCountTransactionLoading = true
      })
      .addCase(DealerMasterService.countTransactionAsyncThunk.rejected, (state) => {
        state.isGetCountTransactionLoading = false
      })
      .addCase(DealerMasterService.countTransactionAsyncThunk.fulfilled, (state, action) => {
        state.isGetCountTransactionLoading = false
        state.fetchedCountTransaction = action.payload
      })
      .addCase(DealerMasterService.getAllDealerTransactionsAsyncThunk.pending, (state) => {
        state.isGetAllDealerTransactionsLoading = true
      })
      .addCase(DealerMasterService.getAllDealerTransactionsAsyncThunk.rejected, (state) => {
        state.isGetAllDealerTransactionsLoading = false
      })
      .addCase(DealerMasterService.getAllDealerTransactionsAsyncThunk.fulfilled, (state, action) => {
        state.isGetAllDealerTransactionsLoading = false
        state.fetchedAllDealerTransactions = action.payload
      })
      .addCase(DealerMasterService.exportDealerTransactionsAsyncThunk.pending, (state) => {
        state.isExportDealerTransactionsLoading = true
      })
      .addCase(DealerMasterService.exportDealerTransactionsAsyncThunk.rejected, (state) => {
        state.isExportDealerTransactionsLoading = false
      })
      .addCase(DealerMasterService.exportDealerTransactionsAsyncThunk.fulfilled, (state) => {
        state.isExportDealerTransactionsLoading = false
      })
      .addCase(DealerMasterService.getTransactionInsuredAsyncThunk.pending, (state) => {
        state.isGetTransactionInsuredLoading = true
      })
      .addCase(DealerMasterService.getTransactionInsuredAsyncThunk.rejected, (state) => {
        state.isGetTransactionInsuredLoading = false
      })
      .addCase(DealerMasterService.getTransactionInsuredAsyncThunk.fulfilled, (state, action) => {
        state.isGetTransactionInsuredLoading = false
        state.fetchedTransactionInsured = action.payload
      })
      .addCase(DealerMasterService.getTransactionSummaryAsyncThunk.pending, (state) => {
        state.isGetTransactionSummaryLoading = true
      })
      .addCase(DealerMasterService.getTransactionSummaryAsyncThunk.rejected, (state) => {
        state.isGetTransactionSummaryLoading = false
      })
      .addCase(DealerMasterService.getTransactionSummaryAsyncThunk.fulfilled, (state, action) => {
        state.isGetTransactionSummaryLoading = false
        state.fetchedTransactionSummary = action.payload
      })
      .addCase(DealerMasterService.getTransactionDetailAsyncThunk.pending, (state) => {
        state.isGetTransactionDetailLoading = true
      })
      .addCase(DealerMasterService.getTransactionDetailAsyncThunk.rejected, (state) => {
        state.isGetTransactionDetailLoading = false
      })
      .addCase(DealerMasterService.getTransactionDetailAsyncThunk.fulfilled, (state, action) => {
        state.isGetTransactionDetailLoading = false
        state.fetchedTransactionDetail = action.payload
      })
      .addCase(DealerMasterService.updateTransactionInsuredAsyncThunk.pending, (state) => {
        state.isUpdateDealerTransactionInsuredLoading = true
      })
      .addCase(DealerMasterService.updateTransactionInsuredAsyncThunk.rejected, (state) => {
        state.isUpdateDealerTransactionInsuredLoading = false
      })
      .addCase(DealerMasterService.updateTransactionInsuredAsyncThunk.fulfilled, (state) => {
        state.isUpdateDealerTransactionInsuredLoading = false
      })
      .addCase(DealerMasterService.cancelInsuredAsyncThunk.pending, (state) => {
        state.isCancelDealerInsuredLoading = true
      })
      .addCase(DealerMasterService.cancelInsuredAsyncThunk.rejected, (state) => {
        state.isCancelDealerInsuredLoading = false
      })
      .addCase(DealerMasterService.cancelInsuredAsyncThunk.fulfilled, (state) => {
        state.isCancelDealerInsuredLoading = false
      })
      .addCase(DealerMasterService.getTransactionCommissionAsyncThunk.pending, (state) => {
        state.isGetTransactionCommissionLoading = true
      })
      .addCase(DealerMasterService.getTransactionCommissionAsyncThunk.rejected, (state) => {
        state.isGetTransactionCommissionLoading = false
      })
      .addCase(DealerMasterService.getTransactionCommissionAsyncThunk.fulfilled, (state, action) => {
        state.isGetTransactionCommissionLoading = false
        state.fetchedTransactionCommission = action.payload
      })
      .addCase(DealerMasterService.getDealerTransactionTaxInvoiceByTransactionIdAsyncThunk.pending, (state) => {
        state.isGetDealerTransactionTaxInvoiceLoading = true
      })
      .addCase(DealerMasterService.getDealerTransactionTaxInvoiceByTransactionIdAsyncThunk.rejected, (state) => {
        state.isGetDealerTransactionTaxInvoiceLoading = false
      })
      .addCase(
        DealerMasterService.getDealerTransactionTaxInvoiceByTransactionIdAsyncThunk.fulfilled,
        (state, action) => {
          state.isGetDealerTransactionTaxInvoiceLoading = false
          state.fetchedDealerTransactionTaxInvoice = action.payload
        }
      )
      .addCase(DealerMasterService.updateDealerTransactionTaxInvoiceAsyncThunk.pending, (state) => {
        state.isUpdateDealerTransactionTaxInvoiceLoading = true
      })
      .addCase(DealerMasterService.updateDealerTransactionTaxInvoiceAsyncThunk.rejected, (state) => {
        state.isUpdateDealerTransactionTaxInvoiceLoading = false
      })
      .addCase(DealerMasterService.updateDealerTransactionTaxInvoiceAsyncThunk.fulfilled, (state) => {
        state.isUpdateDealerTransactionTaxInvoiceLoading = false
      })
      .addCase(DealerMasterService.updateDealerSentTaxInvoiceAsyncThunk.pending, (state) => {
        state.isUpdateDealerSentTaxInvoiceLoading = true
      })
      .addCase(DealerMasterService.updateDealerSentTaxInvoiceAsyncThunk.rejected, (state) => {
        state.isUpdateDealerSentTaxInvoiceLoading = false
      })
      .addCase(DealerMasterService.updateDealerSentTaxInvoiceAsyncThunk.fulfilled, (state) => {
        state.isUpdateDealerSentTaxInvoiceLoading = false
      })
  }
})

export const { resetDealerMasterState, toggleDealerActive, setDealerImage, onChangeDealerColorCodes, setIsEditInfo } =
  slice.actions
export default slice.reducer
